import { Controller } from "@hotwired/stimulus"
import { splitWords, wordTimes, wordAt } from "../utils/spoken_words"

// Dub it in your voice (dubs/show, VideoNotes::Dub). The twin's lip-synced
// video plays silent while its words light up as they're said; you read
// along. The recording starts a beat before the video, and that beat is sent
// with the take so the server trims it - the voice lands where the lips are.
export default class extends Controller {
  static targets = ["video", "words", "record", "stop", "play", "retake", "save", "status", "countdown", "guide"]
  static values = { wordsUrl: String, saveUrl: String }

  async connect() {
    this.audio = new Audio()
    const ready = new Promise((resolve) => {
      if (this.videoTarget.readyState >= 1) resolve()
      else this.videoTarget.addEventListener("loadedmetadata", resolve, { once: true })
    })
    let data = {}
    try {
      const response = await fetch(this.wordsUrlValue, { headers: { Accept: "application/json" } })
      if (response.ok) data = await response.json()
    } catch (_) { /* estimate below */ }
    await ready
    this.words = data.words?.length ? data.words : []
    const duration = this.videoTarget.duration || this.words.length / 2.5
    this.times = Array.isArray(data.times) && data.times.length === this.words.length ? data.times : wordTimes(this.words.length ? this.words : splitWords(""), duration)
    this.wordsTarget.replaceChildren(...this.words.flatMap((word, i) => {
      const span = document.createElement("span")
      span.className = "spoken-word"
      span.textContent = word
      return i ? [document.createTextNode(" "), span] : [span]
    }))
    this.spans = [...this.wordsTarget.querySelectorAll(".spoken-word")]
    this.recordTarget.disabled = false
    this.videoTarget.addEventListener("ended", () => this._ended())
  }

  disconnect() {
    this._halt()
    this.stream?.getTracks().forEach((track) => track.stop())
  }

  async record() {
    this._halt()
    this._show({ stop: true })
    this.statusTarget.textContent = ""
    try {
      this.stream ||= await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
    } catch (_) {
      this._show({ record: true })
      this.statusTarget.textContent = "Allow the microphone to record your take."
      return
    }
    for (const n of [3, 2, 1]) {
      this.countdownTarget.hidden = false
      this.countdownTarget.textContent = n
      await new Promise((resolve) => { this._count = setTimeout(resolve, 700) })
    }
    this.countdownTarget.hidden = true
    this.chunks = []
    this.recorder = new MediaRecorder(this.stream)
    this.recorder.ondataavailable = (event) => { if (event.data.size) this.chunks.push(event.data) }
    this.recorder.onstop = () => this._recorded()
    const started = new Promise((resolve) => { this.recorder.onstart = () => resolve(performance.now()) })
    this.recorder.start()
    const recordingAt = await started
    const video = this.videoTarget
    video.muted = !this.guideTarget.checked
    video.currentTime = 0
    video.addEventListener("playing", () => { this.offset = Math.max(0, performance.now() - recordingAt) }, { once: true })
    await video.play().catch(() => {})
    this.recording = true
    this.statusTarget.textContent = "Recording - read along as the words light up."
    this._follow(() => video.currentTime)
  }

  stop() {
    this.videoTarget.pause()
    this._ended()
  }

  play() {
    this._halt()
    const video = this.videoTarget
    video.muted = true
    video.currentTime = 0
    this.audio.currentTime = Math.min(this.offset / 1000, this.audio.duration || 0)
    Promise.all([video.play(), this.audio.play()]).catch(() => {})
    this._follow(() => video.currentTime)
  }

  async save() {
    if (!this.take) return
    this.saveTarget.disabled = true
    this.statusTarget.textContent = "Saving your take…"
    const form = new FormData()
    form.append("take", this.take, `take.${this.take.type.includes("mp4") ? "m4a" : "webm"}`)
    form.append("offset_ms", Math.round(this.offset || 0))
    try {
      const response = await fetch(this.saveUrlValue, {
        method: "POST", body: form,
        headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
      })
      const result = await response.json()
      if (!response.ok || !result.ok) throw new Error(result.error || "Couldn't save that take. Try again.")
      this.statusTarget.textContent = "Saved - your voice plays with the video now."
      window.location.assign(result.back)
    } catch (error) {
      this.statusTarget.textContent = error.message
      this.saveTarget.disabled = false
    }
  }

  _ended() {
    cancelAnimationFrame(this._raf)
    this._light(-1)
    if (this.recording) {
      this.recording = false
      if (this.recorder?.state === "recording") this.recorder.stop()
    } else {
      this.audio.pause()
    }
  }

  _recorded() {
    this.take = new Blob(this.chunks, { type: this.recorder.mimeType || "audio/webm" })
    this.audio.src = URL.createObjectURL(this.take)
    this.statusTarget.textContent = "Play it back - keep it, or record again."
    this._show({ play: true, retake: true, save: true })
  }

  _follow(clock) {
    cancelAnimationFrame(this._raf)
    const tick = () => {
      this._light(wordAt(this.times, clock()))
      this._raf = requestAnimationFrame(tick)
    }
    tick()
  }

  _light(index) {
    this.spans?.forEach((span, i) => {
      span.classList.toggle("is-spoken", i === index)
      span.classList.toggle("was-spoken", index >= 0 && i < index)
    })
  }

  _halt() {
    clearTimeout(this._count)
    cancelAnimationFrame(this._raf)
    this.audio?.pause()
    this.videoTarget.pause()
  }

  _show(visible) {
    ;["record", "stop", "play", "retake", "save"].forEach((name) => { this[`${name}Target`].hidden = !visible[name] })
    this.saveTarget.disabled = false
  }
}
